// edits.ts — the transcript edit OVERLAY (EGO-132 Phase C.2). Human
// corrections layer on top of the immutable transcript.json + task_events.jsonl
// rather than mutating them: raw STT and the ground-truth sub-task boundaries
// stay byte-pristine, and applyTranscriptEdits() merges the overlay on read.
// Viewers, the editor, and exporters all go through this one function so the
// merge is defined in exactly one tested place.
//
// Time is canonical in boottime ns (the same clock as every other stream). The
// overlay keys each original segment by its t_start_ns — stable because the raw
// transcript never changes — and added segments carry a synthetic id.

import { assignSubtask, type TxSegment, type TaskWindow, type SubtaskTag } from "./transcript.ts";

export interface SegmentEdit {
  text?: string;
  t_start_ns?: number;
  t_end_ns?: number;
  deleted?: boolean;
}

export interface AddedSegment {
  id: string;                 // synthetic stable id (e.g. "add-<n>")
  t_start_ns: number;
  t_end_ns: number;
  text: string;
  subtask?: SubtaskTag;
  kind?: "wake" | "narration";
}

export interface WindowEdit { t_start_ns?: number; t_end_ns?: number }

export interface TranscriptEdits {
  schema: 1;
  tool: string;
  edited_at_ms: number;
  // keyed by the ORIGINAL segment's t_start_ns, as a string
  segments?: Record<string, SegmentEdit>;
  added?: AddedSegment[];
  // sub-task boundary overrides, keyed by window index as a string
  windows?: Record<string, WindowEdit>;
  // optional whole-task envelope override (defaults to first/last window)
  task_bounds?: { t_start_ns?: number; t_end_ns?: number };
}

export interface MergedSegment {
  t_start_ns: number;
  t_end_ns: number;
  start: number;              // seconds from the audio anchor (recomputed)
  end: number;
  text: string;
  kind?: "wake" | "narration";
  subtask?: SubtaskTag;
  // provenance for the editor UI (exporters just filter on `deleted`)
  origKey: string | null;     // original t_start_ns as string, or null if added
  addedId: string | null;     // immutable id when added (stable across re-timing), else null
  added: boolean;
  edited: boolean;            // text or time changed vs the raw segment
  deleted: boolean;
}

export interface MergedWindow extends TaskWindow { edited: boolean }

export interface MergedTranscript {
  segments: MergedSegment[];  // sorted by effective t_start_ns; includes deleted (flagged)
  windows: MergedWindow[];
  taskBounds: { t_start_ns: number; t_end_ns: number } | null;
}

/** An empty overlay — the identity for applyTranscriptEdits. */
export function emptyEdits(nowMs: number, tool = "portal-verify"): TranscriptEdits {
  return { schema: 1, tool, edited_at_ms: nowMs, segments: {}, added: [], windows: {} };
}

const secFrom = (ns: number, anchor: number) => (ns - anchor) / 1e9;

/**
 * Merge an edit overlay onto the raw transcript segments + task windows. Pure.
 * `anchor` is transcript.audio_start_elapsed_ns (to recompute per-segment
 * seconds). Deleted segments are RETAINED in the output with `deleted: true` so
 * the editor can show restore; exporters filter them out. Added segments and
 * boundary/task overrides are applied and everything re-sorted.
 */
export function applyTranscriptEdits(
  segments: TxSegment[], windows: TaskWindow[], edits: TranscriptEdits | null, anchor: number,
): MergedTranscript {
  const segEdits = edits?.segments ?? {};
  const merged: MergedSegment[] = segments.map((s) => {
    const key = String(s.t_start_ns ?? "");
    const e = segEdits[key];
    const t_start_ns = e?.t_start_ns ?? s.t_start_ns ?? 0;
    const t_end_ns = e?.t_end_ns ?? s.t_end_ns ?? t_start_ns;
    const text = e?.text ?? s.text;
    const edited = !!e && (e.text != null || e.t_start_ns != null || e.t_end_ns != null);
    return {
      t_start_ns, t_end_ns, start: secFrom(t_start_ns, anchor), end: secFrom(t_end_ns, anchor),
      text, kind: s.kind, subtask: s.subtask,
      origKey: key, addedId: null, added: false, edited, deleted: !!e?.deleted,
    };
  });

  for (const a of edits?.added ?? []) {
    merged.push({
      t_start_ns: a.t_start_ns, t_end_ns: a.t_end_ns,
      start: secFrom(a.t_start_ns, anchor), end: secFrom(a.t_end_ns, anchor),
      text: a.text, kind: a.kind ?? "narration", subtask: a.subtask,
      origKey: null, addedId: a.id, added: true, edited: false, deleted: false,
    });
  }
  merged.sort((x, y) => x.t_start_ns - y.t_start_ns);

  const winEdits = edits?.windows ?? {};
  const mWindows: MergedWindow[] = windows.map((w) => {
    const e = winEdits[String(w.index)];
    const t_start_ns = e?.t_start_ns ?? w.t_start_ns;
    const t_end_ns = e?.t_end_ns ?? w.t_end_ns;
    return { ...w, t_start_ns, t_end_ns, edited: !!e && (e.t_start_ns != null || e.t_end_ns != null) };
  });

  // Sub-task membership is GEOMETRIC against the (possibly edited) windows, not
  // the frozen Phase-B tag: dragging a boundary re-assigns the chunks that now
  // fall in a different step (EGO-132 C.2 decision). Plain sessions (no windows)
  // keep whatever tag the raw segment carried.
  if (mWindows.length) for (const m of merged) m.subtask = assignSubtask(m as TxSegment, mWindows);

  let taskBounds: MergedTranscript["taskBounds"] = null;
  if (mWindows.length) {
    taskBounds = {
      t_start_ns: edits?.task_bounds?.t_start_ns ?? mWindows[0].t_start_ns,
      t_end_ns: edits?.task_bounds?.t_end_ns ?? mWindows[mWindows.length - 1].t_end_ns,
    };
  } else if (edits?.task_bounds?.t_start_ns != null && edits?.task_bounds?.t_end_ns != null) {
    taskBounds = { t_start_ns: edits.task_bounds.t_start_ns, t_end_ns: edits.task_bounds.t_end_ns };
  }

  return { segments: merged, windows: mWindows, taskBounds };
}

/** The overlay carries at least one real change (worth saving / uploading). */
export function hasEdits(e: TranscriptEdits | null): boolean {
  if (!e) return false;
  return Object.keys(e.segments ?? {}).length > 0
    || (e.added?.length ?? 0) > 0
    || Object.keys(e.windows ?? {}).length > 0
    || (e.task_bounds != null && (e.task_bounds.t_start_ns != null || e.task_bounds.t_end_ns != null));
}
